/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  action,
  selector,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import { Direction } from '@mui/material';
import { Platform } from 'componentregistry';
import { windowSizeAtom } from 'hooks/useWindowSize';
import {
  activePinnedSidebarPanelSelector,
  activeSidebarPanelSelector,
  SidebarPanelName,
} from 'layout/Sidebar/atoms';
import useUser from '../root/useUser';
import { notificationLocation } from 'componentregistry/registry/constants';

export type DarkModeType = 'dark' | 'light' | 'system';

export const darkModeAtom = atom<DarkModeType>({
  path: ['ui', 'options', 'darkMode'],
  defaultValue: 'light',
});

export const userDarkModeAtom = atom<DarkModeType>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'darkMode'],
  defaultValue: 'light',
  onChange: ({ set, value }) => {
    set(darkModeAtom, value);
  },
});

export const applicationDarkModeAtom = atom<DarkModeType>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'darkMode'],
  defaultValue: 'system',
  onChange: ({ set, value }) => {
    set(darkModeAtom, value);
  },
});

export const userDateFormatAtom = atom<string>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'dateFormat'],
  defaultValue: 'MM/dd/yyyy',
});

export const userNumberFormatAtom = atom<string>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'numberFormat'],
  defaultValue: '',
});

export const userDateTimeFormatAtom = atom<string>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'dateTimeFormat'],
  defaultValue: 'MM/dd/yyyy hh:mm a',
});

export const userTimeZoneAtom = atom<string>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'timeZone'],
  defaultValue: '',
});

export const userCSVDateFormatAtom = atom<string>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'csvDateFormat'],
  defaultValue: '%Y-%m-%d',
});

export const userCSVDateTimeFormatAtom = atom<string>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'csvDateTimeFormat'],
  defaultValue: '%Y-%m-%dT%H:%M:%S%.f%:z',
});

export const userLangAtom = atom<string>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'lang'],
  defaultValue: '',
});

export const userDirectionAtom = atom<Direction>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'dir'],
  defaultValue: 'ltr',
});

export const recordDictionaryAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'recordDictionary'],
  defaultValue: false,
});

export const notificationLocationUserSettingsAtom = atom<
  keyof typeof notificationLocation | ''
>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'notificationLocation'],
  defaultValue: '',
});

export function useDirection() {
  const { userName } = useUser();
  return useStateXAtomValue(userDirectionAtom, {
    params: { userName },
  });
}

export function useIsRTL() {
  return useDirection() === 'rtl';
}

export const componentTipAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'highlightComponents'],
  defaultValue: true,
});

export const sidebarLocationOptionAtom = atom({
  path: ['transient', 'ui', 'options', 'sidebarRight'],
  defaultValue: false,
  urlParam: true,
});

export const sidebarLocationPrefAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'sidebarRight'],
  defaultValue: 'start',
});

export const sidebarWidthAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'sidebarWidth'],
  defaultValue: 360,
  onChange: (props) => {
    const { getRef, value, path, data } = props;
    if (data?.timeout) {
      clearTimeout(data.timeout as number);
    }
    if (value !== 360) {
      data.timeout = setTimeout(() => {
        const platform = getRef<Platform>(['platform'])?.current;
        if (platform) {
          platform.postAtom(
            {
              ds: path[1] as string,
              key: path[2] as string,
              path: [path[4]],
              value,
              pk: { userName: path[2] },
            },
            { appUid: platform.appUid },
          );
        } else {
          console.warn('Missing platform ref!');
        }
      }, 3000);
    }
  },
});

export const logoTextAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'logoText'],
  defaultValue: 'CloudIO',
});

export const appLogoImageAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'appLogoImage'],
  defaultValue: '',
});

export const appLoadingAnimAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'appLoadingAnimAtom'],
  defaultValue: 'dotted-spinner',
});

export const appControllerAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'appController'],
  defaultValue: `import React from 'react';
import { Box, Theme, Typography } from '@mui/material';
import type { AppHeaderComponentProps, BaseFunctionProps, IOComponentProps, Platform } from 'cloudio';

function AppComponent(props: IOComponentProps) {
    console.log(props, 'AppComponent.render');
    React.useEffect(() => {
        // fetch and initialize app level data
        console.log(props, 'AppComponent.useEffect');
    }, []);
    return null;
}

function AppHeaderComponent({
  activeUsers,
  darkModeToggle,
  fullScreenToggle,
  loadingIndicator,
  logoText,
  orgLogo,
  pageMenuIcon,
  sidebarIcon,
  subTitle,
  userProfileIcon
}: AppHeaderComponentProps) {
  return <Box sx={{
      width: '100%',
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 20px',
      gap: 1,
  }}>
      {orgLogo}
      {sidebarIcon}
      <Typography variant="h6">{logoText}</Typography>
      <Typography variant="body2">{subTitle}</Typography>
      <Box sx={{ flex: 1 }} />
      {loadingIndicator}
      {activeUsers}
      {darkModeToggle}
      {fullScreenToggle}
      {pageMenuIcon}
      {userProfileIcon}
  </Box>;
}

export default {
    // refer https://mui.com/system/the-sx-prop/
    getAppSX: async ({ theme, platform }: BaseFunctionProps) => {
        const darkMode = theme.palette.mode === 'dark';
        /*
        return {
            bgcolor: darkMode ? '#000' : '#fff',
            '& #someId': { display: 'none' },
            '& .someCSSClassName': { backgroundColor: theme.palette.background.paper }
        };
        */
        return {};
    },
    someCustomAppLevelUtilFunction: async () => {
        // you can invoke this function from any page controller
        // import { someCustomAppLevelUtilFunction } from 'app';
        // await someCustomAppLevelUtilFunction();
    },
    // AppComponent, // AppComponent will be rendered at the root of the application and will remain mounted on all Pages within this Application
    // AppHeaderComponent, // AppHeaderComponent will be rendered at the top replacing the default Header
}`,
});

export const sidebarWidthSelector = selector<number>({
  path: ['transient', 'IoUserSettings', ':userName', ':appUid', 'sidebarWidth'],
  get: ({ get, params }) => {
    if (!params.appUid) {
      throw new Error('Missing appUid in sidebarWidthSelector!');
    }
    let activeSidebarPanel = get(activeSidebarPanelSelector, {
      params,
    });
    const pinnedSidebarPanel = get(activePinnedSidebarPanelSelector, {
      params,
    });
    if (activeSidebarPanel === pinnedSidebarPanel) {
      activeSidebarPanel = 'HIDDEN';
    }
    if (!activeSidebarPanel || activeSidebarPanel === 'HIDDEN') {
      return 0;
    }
    const maximized = get(maximizeSidebarWidthAtom) === 'Y';
    const { width: ww } = get(windowSizeAtom);
    let width = ww - 40;
    if (width < 600) {
      // make the sidebars full screen on smaller screens
      return width;
    }
    if (!maximized) {
      width = Math.min(width - 300, get(sidebarWidthAtom, { params }));
    }
    return width;
  },
  set: ({ set, params }, width) => {
    set(sidebarWidthAtom, width, { params });
    return width;
  },
});

// wrapper Atom for dbPinnedSidebarWidthAtom to prevent multiple db updates on resize
export const pinnedSidebarWidthAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'pinnedSidebarWidth'],
  defaultValue: 360,
  onChange: (props) => {
    const { getRef, value, path, data } = props;
    if (data?.timeout) {
      clearTimeout(data.timeout as number);
    }
    if (value !== 360) {
      data.timeout = setTimeout(() => {
        const platform = getRef<Platform>(['platform'])?.current;
        if (platform) {
          platform.postAtom(
            {
              ds: path[1] as string,
              key: path[2] as string,
              path: [path[4]],
              value,
              pk: { userName: path[2] },
            },
            { appUid: platform.appUid },
          );
        } else {
          console.warn('Missing platform ref!');
        }
      }, 3000);
    }
  },
});

export const tooltipsDelayAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'tooltipsDelay'],
  defaultValue: 1000,
});

export const toolbarItemsAtom = atom<SidebarPanelName[]>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'toolbarItems'],
  // users and wf must always be in the defaultValue list as they
  // are visible for user admin & wf admin roles and the more tools
  // option will not be visible if the user have only those roles
  defaultValue: [
    'pages',
    'ds',
    'tree',
    'props',
    'functions',
    'users',
    'apps',
    'wf',
  ],
});

export const pinnedSidebarWidthSelector = selector<number>({
  path: [
    'transient',
    'IoUserSettings',
    ':userName',
    ':appUid',
    'pinnedSidebarWidth',
  ],
  get: ({ get, params }) => {
    const maximized = get(maximizeSidebarWidthAtom) === 'Y';
    const panel = get(activePinnedSidebarPanelSelector, { params });
    const activeSidebarPanel = get(activeSidebarPanelSelector, {
      params,
    });

    const { width: ww } = get(windowSizeAtom);
    let width = ww - 40;
    if (width > 600) {
      // make the sidebars full screen on smaller screens
      if (!maximized) {
        width = Math.min(width - 300, get(pinnedSidebarWidthAtom, { params }));
      }
    }

    return (maximized && activeSidebarPanel && activeSidebarPanel !== panel) ||
      !panel ||
      panel === 'HIDDEN'
      ? 0
      : width;
  },
  set: ({ set, params }, width) => {
    set(pinnedSidebarWidthAtom, width, { params });
    return width;
  },
});

export const pinPropertyPanelOptionAtom = atom({
  path: ['ui', 'options', 'pinPropertiesOnRight'],
  defaultValue: true,
});

export const subTitleAtom = atom({
  path: ['ui', 'header', 'subTitle'],
  defaultValue: '',
});

export const darkModeAction = action(({ set }) => {
  set(darkModeAtom, 'dark');
});

export const maximizeSidebarWidthAtom = atom({
  path: ['transient', 'ui', 'options', 'maximizeSidebarWidth'],
  defaultValue: 'N',
  urlParam: true,
});
